/**
 * 设置中心 · 业务设置域接口（`sys.plan` 套餐订阅 + `sys.pay` 支付渠道）
 *
 * `sys.plan` 已切到 platform 的页面契约（12-07-06 / 12-07-07），路径前缀
 * `/admin/platform`，全部走网关。页面只渲染服务端给的值：
 * 用量比例、档位能不能选、缴费记录每一行允许哪些动作，都在响应里，不在页面里再判。
 *
 * `sys.pay` 已切到 billing 的收款账户契约，接口在同目录的 `muou-pay-channel.ts`。
 */
import type {
  AdminModuleGateAvailabilityVO,
  PlanAutoRenewSettingReq,
  PlanAutoRenewVO,
  PlanCancelPaymentResp,
  PlanCreateSubscriptionReq,
  PlanCreateSubscriptionResp,
  PlanEnableAutoRenewReq,
  PlanEnableAutoRenewResp,
  PlanModuleGatePageVO,
  PlanOptionListVO,
  PlanPayMethodDefaultResp,
  PlanPayMethodDeleteResp,
  PlanPayMethodKindListVO,
  PlanPayMethodLedgerVO,
  PlanPayMethodOptionListVO,
  PlanPayMethodSaveReq,
  PlanPayMethodSaveResp,
  PlanPaymentPageVO,
  PlanPayResultVO,
  PlanReceiptRequestResp,
  PlanReceiptVO,
  PlanRetryPaymentResp,
  PlanSubscriptionDetailVO,
  PlanUsageVO,
} from '@/types';
import { gatewayRequest } from '@/utils/http';

/* -------------------------------------------------- 套餐订阅 -------------------------------------------------- */

/** 当前订阅详情（API-12-037）：进页第一读，无入参 */
export const getPlanDetail = () =>
  gatewayRequest<PlanSubscriptionDetailVO>(
    '/admin/platform/subscription/getSubscriptionDetail',
    { method: 'POST', data: {} },
  );

/**
 * 配额用量（API-12-038）：人员与后台帐号实算、存储读快照。
 * 存储那一条的 `used` 在还没有统计快照时为 null，页面照实说明，不凑数字。
 */
export const getPlanUsage = () =>
  gatewayRequest<PlanUsageVO>('/admin/platform/usage/sumUsageMetrics', {
    method: 'POST',
    data: {},
  });

/**
 * 功能模块可用状态（API-12-039）：本会全部模块的开放情况。
 * 模块登记表条数有限，一次取到服务端上限（200），页面按全量清单展示、不再分页。
 */
export const pagePlanModuleGates = () =>
  gatewayRequest<PlanModuleGatePageVO>(
    '/admin/platform/moduleGate/pageModuleGates',
    { method: 'POST', data: { page: 1, size: 200 } },
  );

/**
 * 本会当前档位 + 全量模块可用性：导航置灰与拦截页的数据源。
 *
 * 判定与套餐页的 `pageModuleGates` 同一处（platform），前端只按 `available` 展示，
 * 不自己比档位序。该前缀在网关门控放行清单里，所以没买模块也能取到。
 */
export const getModuleGateAvailability = () =>
  gatewayRequest<AdminModuleGateAvailabilityVO>(
    '/admin/platform/moduleGate/detailAvailability',
    // 门控取不到按全亮放行：不能因为平台没部署、或当前是平台入口租户（99404000）
    // 就在每次登录时弹一条全局错误，把人挡在门外（同 useMenuBadges 的静默口径）
    { method: 'POST', data: {}, skipErrorHandler: true },
  );

/**
 * 档位对比（API-12-054）。`includeDisabled` 不传时按服务端开关的默认档走，
 * 页面不自己决定停售档位藏不藏。
 */
export const listPlanOptions = (includeDisabled?: boolean) =>
  gatewayRequest<PlanOptionListVO>('/admin/platform/plan/listPlanOptions', {
    method: 'POST',
    data: { includeDisabled },
  });

/** 缴费记录分页（API-12-040）：合计金额与列表同一筛选，页面不另算 */
export const pagePlanPayments = (data: {
  page?: number;
  size?: number;
  status?: string;
  subKind?: string;
  year?: number;
  hasReceipt?: boolean;
  kw?: string;
  sort?: string;
}) =>
  gatewayRequest<PlanPaymentPageVO>(
    '/admin/platform/subscription/pageSubscriptionPayments',
    { method: 'POST', data },
  );

/** 可选缴费方式（API-12-053）：续费与升级两个入口传 `autoableOnly=false` */
export const listPlanPayMethods = (autoableOnly = false) =>
  gatewayRequest<PlanPayMethodOptionListVO>(
    '/admin/platform/payMethod/listPayMethodOptions',
    { method: 'POST', data: { autoableOnly } },
  );

/**
 * 申请续费 / 升级（API-12-041）：只落一条待缴费记录，不当场改档位。
 * 金额、顺延后的到期日、升级差价全由服务端算，回执里带回来。
 */
export const createPlanSubscription = (data: PlanCreateSubscriptionReq) =>
  gatewayRequest<PlanCreateSubscriptionResp>(
    '/admin/platform/subscription/createSubscription',
    { method: 'POST', data },
  );

/**
 * 发起缴付（API-12-075）。界面上那颗按钮是「模拟缴付成功」，只在假支付渠道开着时
 * 由 API-12-040 的 `actions[]` 下发；到账后档位/到期日由服务端在同一事务里改。
 */
export const payPlanSubscription = (subNo: string, mockOutcome = 'success') =>
  gatewayRequest<PlanPayResultVO>(
    '/admin/platform/subscription/paySubscriptionPayment',
    { method: 'POST', data: { subNo, mockOutcome } },
  );

/** 取消待缴费记录（API-12-042）：置状态不删行，版本对不上即拒 */
export const cancelPlanPayment = (subNo: string, version: number) =>
  gatewayRequest<PlanCancelPaymentResp>(
    '/admin/platform/subscription/cancelSubscriptionPayment',
    { method: 'POST', data: { subNo, version } },
  );

/** 重试扣款（API-12-043）：只有扣款失败的行能重试 */
export const retryPlanPayment = (subNo: string, version: number) =>
  gatewayRequest<PlanRetryPaymentResp>(
    '/admin/platform/subscription/retrySubscriptionPayment',
    { method: 'POST', data: { subNo, version } },
  );

/** 索取收据（API-12-044）：只发申请，本会开不出这张票 */
export const requestPlanReceipt = (subNo: string) =>
  gatewayRequest<PlanReceiptRequestResp>(
    '/admin/platform/receipt/submitReceiptRequest',
    { method: 'POST', data: { subNo } },
  );

/** 收据票面（API-12-045）：全只读 */
export const getPlanReceipt = (receiptNo: string) =>
  gatewayRequest<PlanReceiptVO>(
    '/admin/platform/receipt/getSubscriptionReceipt',
    { method: 'POST', data: { receiptNo } },
  );

/**
 * 下载收据（API-12-046）：全系统唯一用 GET 的文件下载，出参不套 Result 信封。
 * 失败时响应体也是文件流而不是 JSON，全局错误处理读不出服务端那句原因，
 * 所以跳过它、由调用方把那层 Blob 解出来再提示。
 */
export const downloadPlanReceipt = (receiptNo: string) =>
  gatewayRequest<Blob>(
    '/admin/platform/receipt/downloadSubscriptionReceipt',
    {
      method: 'GET',
      params: { receiptNo },
      responseType: 'blob',
      skipErrorHandler: true,
    },
  );

/* -------------------------------------------------- 支付方式台账 -------------------------------------------------- */

/** 支付方式列表（API-12-048）：数量个位数、不分页 */
export const pagePlanPayMethods = () =>
  gatewayRequest<PlanPayMethodLedgerVO>(
    '/admin/platform/payMethod/pagePayMethods',
    { method: 'POST', data: {} },
  );

/** 支付方式类型选项源（API-12-052） */
export const listPlanPayMethodKinds = (enabledOnly = true) =>
  gatewayRequest<PlanPayMethodKindListVO>(
    '/admin/platform/payMethodKind/listPayMethodKindOptions',
    { method: 'POST', data: { enabledOnly } },
  );

/** 新增支付方式（API-12-049） */
export const createPlanPayMethod = (data: PlanPayMethodSaveReq) =>
  gatewayRequest<PlanPayMethodSaveResp>(
    '/admin/platform/payMethod/createPayMethod',
    { method: 'POST', data },
  );

/** 编辑支付方式（API-12-049）：与新增同一处实现 */
export const updatePlanPayMethod = (data: PlanPayMethodSaveReq) =>
  gatewayRequest<PlanPayMethodSaveResp>('/admin/platform/payMethod/update', {
    method: 'POST',
    data,
  });

/** 设为默认支付方式（API-12-050） */
export const setDefaultPlanPayMethod = (payMethodId: string) =>
  gatewayRequest<PlanPayMethodDefaultResp>(
    '/admin/platform/payMethod/default/updateDefaultPayMethod',
    { method: 'POST', data: { payMethodId } },
  );

/** 删除支付方式（API-12-051）：被自动续费或待缴费引用时服务端拒绝 */
export const deletePlanPayMethod = (payMethodId: string, version: number) =>
  gatewayRequest<PlanPayMethodDeleteResp>(
    '/admin/platform/payMethod/deletePayMethod',
    { method: 'POST', data: { payMethodId, version } },
  );

/* -------------------------------------------------- 自动续费 -------------------------------------------------- */

/** 自动续费设置（API-12-047）：开启与关闭同一接口 */
export const updatePlanAutoRenew = (data: PlanAutoRenewSettingReq) =>
  gatewayRequest<PlanAutoRenewVO>(
    '/admin/platform/autoRenew/updateAutoRenewSetting',
    { method: 'POST', data },
  );

/** 开启自动续费（API-12-055）：不弹表单，方式由服务端挑 */
export const enablePlanAutoRenew = (data: PlanEnableAutoRenewReq) =>
  gatewayRequest<PlanEnableAutoRenewResp>(
    '/admin/platform/autoRenew/enableAutoRenew',
    { method: 'POST', data },
  );
